import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Overlay } from '@angular/cdk/overlay';
import { NavigationService } from '../services/navigation.service';
import { DrawerService } from '../services/drawer.service';
import { GroupService } from '../services/group.service';
import { HeaderComponent } from '../navigation/header/header.component';
import { DrawerComponent } from '../drawer/drawer/drawer.component';
import { UiSheetShellComponent } from '../ui/sheet/sheet-shell.component';
import { DrawerContentComponent } from '../drawer/drawer-content/drawer-content.component';
import { DRAWER_TITLE_ID } from '../drawer/drawer-layout/drawer-layout.component';
import { UiDialogService } from '../ui/dialog/dialog.service';

@Component({
  selector: 'app-page-template',
  host: { '(document:keydown.escape)': 'onMobileEscape($event)' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './page-template.component.html',
  styleUrl: './page-template.component.scss',
  imports: [
    HeaderComponent,
    DrawerComponent,
    UiSheetShellComponent,
    DrawerContentComponent,
  ],
})
export class PageTemplateComponent {
  nav = inject(NavigationService);
  drawer = inject(DrawerService);
  groupService = inject(GroupService);
  dialog = inject(UiDialogService);

  readonly showHeader = input(true);
  /** Names the drawer sheet and mobile panel by the open drawer's visible title. */
  protected readonly drawerTitleId = DRAWER_TITLE_ID;
  readonly mobilePanelRendered = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly mobilePanel = viewChild<ElementRef<HTMLElement>>('mobilePanel');
  private readonly mobileScrollStrategy = inject(Overlay).scrollStrategies.block();

  constructor() {
    effect(onCleanup => {
      if (this.drawer.bottomSheetOpen()) {
        this.mobilePanelRendered.set(true);
        return;
      }
      let canceled = false;
      // Keep portal content mounted until the CSS exit finishes. No animations (including
      // reduced motion) completes immediately; reopening cancels this pending removal.
      const closeAfterRender = afterNextRender(
        () => {
          const animations = this.mobilePanel()?.nativeElement.getAnimations?.() ?? [];
          void Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
            if (!canceled) this.mobilePanelRendered.set(false);
          });
        },
        { injector: this.injector },
      );
      onCleanup(() => {
        canceled = true;
        closeAfterRender.destroy();
      });
    });
    effect(onCleanup => {
      if (!this.mobilePanelRendered()) return;
      const opener = this.document.activeElement as HTMLElement | null;
      let panel: HTMLElement | undefined;
      this.mobileScrollStrategy.enable();
      // Focus the named region without trapping focus or opening the mobile keyboard.
      const focusAfterRender = afterNextRender(
        () => {
          panel = this.mobilePanel()?.nativeElement;
          panel?.focus({ preventScroll: true });
        },
        { injector: this.injector },
      );
      onCleanup(() => {
        focusAfterRender.destroy();
        this.mobileScrollStrategy.disable();
        if (this.destroyRef.destroyed) return;
        const activeElement = this.document.activeElement;
        if (activeElement !== this.document.body && !panel?.contains(activeElement)) return;
        // Wait until the covered page is no longer inert. Preserve focus moved to the header or a modal.
        afterNextRender(
          () => {
            if (opener?.isConnected && !opener.closest('[inert]')) opener.focus({ preventScroll: true });
          },
          { injector: this.injector },
        );
      });
    });
  }

  onMobileEscape(event: Event) {
    if (!this.drawer.bottomSheetOpen() || this.dialog.modalOpen() || event.defaultPrevented) return;
    event.preventDefault();
    this.onBottomSheetClosed();
  }

  get drawerOpen(): boolean {
    return this.drawer.navOpen();
  }

  get desktopDrawerActive(): boolean {
    return this.drawer.desktopDrawerActive();
  }

  get suppressGlobalHeader(): boolean {
    return this.nav.currentRouteSignal().startsWith('/budgets') && this.groupService.reorderingSignal();
  }

  get headerVisible(): boolean {
    return this.showHeader() && !this.suppressGlobalHeader;
  }

  get rightDrawerOverlayClass(): string {
    return `${this.headerVisible ? '!top-nav' : '!top-0'} pointer-events-auto`;
  }

  get rightDrawerContentClass(): string {
    // Helm's right sheet defaults to a padded, 3/4-width panel; the drawer is a flush,
    // token-width column that starts below the header.
    const topClass = this.headerVisible ? '!top-nav' : '!top-0';
    return `${topClass} !bottom-0 !h-auto !min-h-0 !w-drawer-shell !max-w-none !gap-0 !bg-surface !p-0 !shadow-overlay`;
  }

  private completeDrawerClose() {
    this.drawer.navOpen.set(false);
    this.drawer.completeDrawerClose();
  }

  onDrawerSheetClosed() {
    this.completeDrawerClose();
  }

  onBottomSheetClosed() {
    this.drawer.closeMobileSheet();
    this.drawer.completeDrawerClose();
  }

  closeSidenav() {
    this.nav.sideNav = false;
  }
}
